import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell, AlertTriangle, ChevronRight, Check, Clock, AlertCircle, Info, 
  PartyPopper, Target, Wallet, ArrowUp, ArrowDown, Minus, X, Send, ShieldCheck, History, TrendingUp
} from "lucide-react";
import {
  ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip
} from "recharts";
import { useApi } from "../hooks/useApi";
import { useQontoSilentSync } from "../hooks/useQontoSilentSync";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { DonutChart } from "../components/charts/DonutChart";
import { DemoContextMessage } from "../components/DemoConversionPopup";
import { UrssafSetupModal } from "../components/urssaf/UrssafSetupModal";
import { UrssafDeclareModal } from "../components/urssaf/UrssafDeclareModal";
import { SimulateurShortcut } from "../components/dashboard/SimulateurShortcut";
import { UrssafOnboardingBanner } from "../components/urssaf/UrssafOnboardingBanner";
import { UrssafConnectLogo } from "../components/urssaf/UrssafBrandingKit";
import { ReferralInviteBanner } from "../components/dashboard/ReferralInviteBanner";
import { formatCurrency } from "../utils/formatters";
import { getPlafond, getSeuilTVA, getBeneficeType, getAbattement, getTrimestreEmoji } from "../utils/helpers";
import { COLORS } from "../utils/constants";

// Dashboard Page
export const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const api = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { loadDashboard(); loadUrssafStatus(); loadHistory(); }, []);
  // Sync Qonto silencieuse à l'ouverture du Dashboard (effet temps réel sans charger les serveurs)
  useQontoSilentSync(() => loadDashboard());

  const [urssafStatus, setUrssafStatus] = useState(null);
  const [urssafHistory, setUrssafHistory] = useState([]);
  const [showUrssafSetup, setShowUrssafSetup] = useState(false);
  const [showUrssafDeclare, setShowUrssafDeclare] = useState(false);
  const [showUrssafHistory, setShowUrssafHistory] = useState(false);

  const loadUrssafStatus = async () => {
    try {
      const r = await api.get("/urssaf/status");
      setUrssafStatus(r.data);
    } catch { /* silent */ }
  };
  const loadHistory = async () => {
    try {
      const r = await api.get("/urssaf/declarations");
      setUrssafHistory(r.data?.declarations || []);
    } catch { /* silent */ }
  };

  const loadDashboard = async () => {
    try {
      const response = await api.get("/dashboard");
      setData(response.data);
    } catch (err) { toast.error("Erreur de chargement"); }
    setLoading(false);
  };

  const handleMarkDeclarationDone = async () => {
    try {
      const response = await api.post("/declaration/marquer-faite");
      if (response.data.fait) {
        toast.success("Déclaration marquée comme faite !");
      } else {
        toast.info("Déclaration marquée comme non faite");
      }
      loadDashboard();
    } catch (err) { toast.error("Erreur"); }
  };

  // Gestion des alertes masquées (stockées dans localStorage)
  const [hiddenAlerts, setHiddenAlerts] = useState(() => {
    const saved = localStorage.getItem('biz360_hidden_alerts');
    return saved ? JSON.parse(saved) : [];
  });

  const toggleAlertVisibility = (alerteKey) => {
    setHiddenAlerts(prev => {
      const newHidden = prev.includes(alerteKey) 
        ? prev.filter(k => k !== alerteKey)
        : [...prev, alerteKey];
      localStorage.setItem('biz360_hidden_alerts', JSON.stringify(newHidden));
      return newHidden;
    });
  };

  const getAlertKey = (alerte) => `${alerte.categorie}_${alerte.message?.substring(0, 30)}`;

  const visibleAlertes = data?.alertes?.filter(a => !hiddenAlerts.includes(getAlertKey(a))) || [];
  const hiddenAlertesCount = (data?.alertes?.length || 0) - visibleAlertes.length;

  if (loading) {
    return (<DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div></DashboardLayout>);
  }

  const caComparison = data?.resume?.ca_mensuel > (data?.resume?.ca_mois_precedent || 0) ? "up" : 
                       data?.resume?.ca_mensuel < (data?.resume?.ca_mois_precedent || 0) ? "down" : "equal";

  // Données pour le camembert (mutualisation résultat net + répartition)
  const pieData = [
    { name: 'Résultat Net', value: Math.max(0, data?.resume?.resultat_net || 0), fill: COLORS.text },
    { name: 'Cotisations', value: (data?.resume?.cotisations_totales || 0) + (data?.resume?.versement_liberatoire || 0), fill: COLORS.principale },
    { name: 'Dépenses', value: data?.resume?.depenses_annuelles || 0, fill: COLORS.accent }
  ].filter(d => d.value > 0);
  const totalPie = pieData.reduce((sum, d) => sum + d.value, 0);

  // Données pour graphique courbe CA
  const moisLabels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
  const annee = new Date().getFullYear();
  const caLineData = moisLabels.map((mois, i) => ({
    mois,
    principal: data?.graphiques?.ca_par_mois_principal?.[`${annee}-${String(i+1).padStart(2,'0')}`] || 0,
    secondaire: data?.graphiques?.ca_par_mois_secondaire?.[`${annee}-${String(i+1).padStart(2,'0')}`] || 0,
    total: data?.graphiques?.ca_par_mois?.[`${annee}-${String(i+1).padStart(2,'0')}`] || 0
  }));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Bannière onboarding Urssaf : "gagne du temps, automatise" (si SIRET/DOB manquants) */}
        <UrssafOnboardingBanner />

        {/* Bannière compte démo */}
        {user?.email?.toLowerCase() === 'demo@biz360.fr' && (
          <div className="p-4 rounded-xl bg-gradient-to-r from-purple-100 to-orange-100 border-2 border-purple-300">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🎮</span>
                <div>
                  <span className="font-bold text-purple-700">Mode Démonstration</span>
                  <p className="text-sm text-muted-foreground">
                    Tu explores Biz360 avec des données fictives. Crée ton compte pour gérer ta vraie activité !
                  </p>
                </div>
              </div>
              <a href="/subscription" className="px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors">
                Créer mon compte
              </a>
            </div>
          </div>
        )}

        {/* Bandeau viral parrainage (masqué pour le compte démo) */}
        {user?.email?.toLowerCase() !== 'demo@biz360.fr' && <ReferralInviteBanner />}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{color: COLORS.text}}>
              ✨ Hello {user?.prenom}
            </h1>
            <p className="text-muted-foreground">Mon Tableau de Bord</p>
          </div>
          <div className="text-sm text-muted-foreground bg-muted px-4 py-2 rounded-lg">
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>

        {/* Espacement entre date et bandeau factures */}
        <div className="h-4"></div>

        {/* Bandeau Factures - sous la date */}
        {data?.factures_impayees?.length > 0 ? (
          <div className="p-4 rounded-xl flex items-center justify-between" style={{backgroundColor: COLORS.error + '15', border: `2px solid ${COLORS.error}`}}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <span className="font-bold" style={{color: COLORS.error}}>{data.factures_impayees.length} facture(s) à relancer</span>
                <span className="text-sm ml-2 text-muted-foreground">
                  {data.factures_impayees.slice(0, 2).map(f => f.client).join(', ')}
                  {data.factures_impayees.length > 2 && '...'}
                </span>
              </div>
            </div>
            <button onClick={() => navigate('/ventes')} className="px-4 py-2 rounded-lg text-sm font-medium" style={{backgroundColor: COLORS.error, color: 'white'}}>
              Voir détails
            </button>
          </div>
        ) : (
          <div className="p-2 rounded-lg flex items-center gap-2" style={{backgroundColor: COLORS.success + '15', border: `1px solid ${COLORS.success}40`}}>
            <span className="text-base">✅</span>
            <span className="text-xs" style={{color: COLORS.success}}>Aucune facture en retard</span>
          </div>
        )}

        {/* Alertes - DÉPLACÉ ICI sous le bandeau factures - écriture plus petite */}
        {(visibleAlertes.length > 0 || hiddenAlertesCount > 0) && (
          <div className="rounded-lg border p-3 space-y-1" style={{backgroundColor: COLORS.bg}}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium flex items-center gap-1" style={{color: COLORS.accent}}>
                <AlertTriangle size={14} style={{color: COLORS.warning}} /> ALERTES 
                {visibleAlertes.length > 0 && <span>({visibleAlertes.length})</span>}
              </span>
              {hiddenAlertesCount > 0 && (
                <button 
                  onClick={() => {
                    setHiddenAlerts([]);
                    localStorage.removeItem('biz360_hidden_alerts');
                  }}
                  className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition"
                  style={{backgroundColor: COLORS.accent + '20', color: COLORS.accent}}
                >
                  Réafficher ({hiddenAlertesCount})
                </button>
              )}
            </div>
            {visibleAlertes.length === 0 ? (
              <div className="text-center py-2 text-muted-foreground text-xs">
                ✨ Alertes masquées
              </div>
            ) : (
              <div className="space-y-1">
                {visibleAlertes.map((alerte, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded text-xs" 
                       style={{
                         backgroundColor: alerte.type === 'error' ? COLORS.error + '10' : alerte.type === 'warning' ? COLORS.warning + '10' : COLORS.accent + '10',
                         borderLeft: `3px solid ${alerte.type === 'error' ? COLORS.error : alerte.type === 'warning' ? COLORS.warning : COLORS.accent}`
                       }}>
                    {alerte.type === 'error' ? <AlertCircle size={14} style={{color: COLORS.error}} /> : alerte.type === 'warning' ? <AlertTriangle size={14} style={{color: COLORS.warning}} /> : <Info size={14} style={{color: COLORS.accent}} />}
                    <span className="font-medium">{alerte.categorie}:</span>
                    <span className="flex-1">{alerte.message}</span>
                    <button 
                      onClick={() => toggleAlertVisibility(getAlertKey(alerte))}
                      className="p-1 rounded-full hover:bg-gray-200 transition"
                      title="Masquer"
                    >
                      <X size={12} className="text-muted-foreground" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Section 1: MON CA + MON RÉSULTAT (col gauche)  |  MA DÉCLARATION Urssaf (col droite) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

          {/* Colonne gauche : MON CA empilé sur MON RÉSULTAT */}
          <div className="space-y-6">

          {/* 1. MON CA */}
          <div className="rounded-xl border p-6 cursor-pointer hover:shadow-md transition-shadow" 
               style={{backgroundColor: COLORS.bg}} 
               onClick={() => navigate('/recap-ca')}>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2" style={{color: COLORS.accent}}>
              <Target size={20} style={{color: COLORS.principale}} /> MON CA
            </h3>
            
            <DonutChart 
              principal={data?.resume?.ca_annuel_principal || 0}
              secondaire={data?.resume?.ca_annuel_secondaire || 0}
              plafondPrincipal={getPlafond(user?.type_activite)}
              plafondSecondaire={getPlafond(user?.type_activite_secondaire)}
              hasDoubleActivite={user?.double_activite}
            />

            <div className="mt-4 space-y-3">
              {/* CA Annuel avec reste avant plafond */}
              <div className="text-center">
                <span className="text-2xl font-bold" style={{color: COLORS.text}}>{formatCurrency(data?.resume?.ca_annuel)}</span>
                <div className="text-xs text-muted-foreground">CA Annuel</div>
              </div>
              
              {/* Reste avant plafond - Principale */}
              <div className="flex items-center justify-between p-2 rounded-lg" style={{backgroundColor: COLORS.principale + '15'}}>
                <span className="text-sm" style={{color: COLORS.principale}}>Principale (Reste avant plafond)</span>
                <span className="font-bold" style={{color: COLORS.principale}}>{formatCurrency(data?.plafond_ae?.reste)}</span>
              </div>
              
              {/* Reste avant plafond - Secondaire */}
              {user?.double_activite && (
                <div className="flex items-center justify-between p-2 rounded-lg" style={{backgroundColor: COLORS.accent + '15'}}>
                  <span className="text-sm" style={{color: COLORS.accent}}>Secondaire (Reste avant plafond)</span>
                  <span className="font-bold" style={{color: COLORS.accent}}>{formatCurrency(data?.plafond_ae_secondaire?.reste)}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center p-3 rounded-lg cursor-pointer hover:opacity-80 transition-opacity" 
                   style={{backgroundColor: COLORS.principale + '30'}}
                   onClick={(e) => { e.stopPropagation(); navigate('/ventes'); }}>
                <span className="flex items-center gap-2">
                  Ce mois
                  {caComparison === "up" && <ArrowUp style={{color: COLORS.success}} size={16} />}
                  {caComparison === "down" && <ArrowDown style={{color: COLORS.error}} size={16} />}
                  {caComparison === "equal" && <Minus className="text-gray-500" size={16} />}
                </span>
                <span className="font-bold">{formatCurrency(data?.resume?.ca_mensuel)}</span>
              </div>
            </div>
          </div>

          {/* 2. MON RÉSULTAT — sous MON CA (col gauche) */}
          {totalPie > 0 && (
            <div className="rounded-xl border p-6" style={{backgroundColor: COLORS.bg}} data-testid="dashboard-mon-resultat-card">
              <h3 className="font-semibold mb-4 flex items-center gap-2" style={{color: COLORS.accent}}>
                <TrendingUp size={20} /> MON RÉSULTAT
              </h3>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={25} outerRadius={40} dataKey="value">
                        {pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>CA Brut</span>
                    <span className="font-medium">{formatCurrency(data?.resume?.ca_annuel)}</span>
                  </div>
                  <div className="flex justify-between" style={{color: COLORS.error}}>
                    <span>- Dépenses (hors cotis.)</span>
                    <span>-{formatCurrency((data?.resume?.depenses_annuelles || 0) - (data?.resume?.depenses_urssaf || 0))}</span>
                  </div>
                  <div className="flex justify-between" style={{color: COLORS.principale}}>
                    <span>- Cotisations</span>
                    <span>-{formatCurrency((data?.resume?.cotisations_totales || 0) + (data?.resume?.versement_liberatoire || 0))}</span>
                  </div>
                  <hr className="border-dashed" />
                  <div className="flex justify-between p-1 rounded" style={{backgroundColor: data?.resume?.resultat_net >= 0 ? COLORS.success + '15' : COLORS.error + '15'}}>
                    <span className="font-bold">Net</span>
                    <span className="font-bold" style={{color: data?.resume?.resultat_net >= 0 ? COLORS.success : COLORS.error}}>
                      {formatCurrency(data?.resume?.resultat_net)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          </div>
          {/* Fin colonne gauche */}

          {/* 3. MA DÉCLARATION Urssaf */}
          <div className="rounded-xl border p-6 relative" style={{backgroundColor: COLORS.bg}}>
            {/* Badge "en certification" — rappel que l'intégration URSSAF arrive bientôt pour tous */}
            <div
              className="absolute -top-2.5 right-4 px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1 shadow-sm"
              style={{backgroundColor: COLORS.accent, color: 'white'}}
              data-testid="urssaf-coming-soon-badge"
              title="L'intégration officielle Urssaf TDAE sera accessible à tous les auto-entrepreneurs bientôt"
            >
              <ShieldCheck size={10} /> Bientôt disponible pour tous
            </div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2" style={{color: COLORS.accent}}>
                <Bell size={20} /> <span data-testid="urssaf-block-title">MA DÉCLARATION Urssaf</span>
              </h3>
              <UrssafConnectLogo size={40} />
            </div>
            
            {/* Mini alerte si pas faite et urgente */}
            {!data?.declaration_urssaf?.fait && data?.declaration_urssaf?.jours_restants <= 15 && (
              <div className="mb-4 p-3 rounded-lg flex items-center gap-2 animate-pulse" style={{backgroundColor: COLORS.error + '20', border: `2px solid ${COLORS.error}`}}>
                <AlertCircle style={{color: COLORS.error}} size={20} />
                <span className="font-bold" style={{color: COLORS.error}}>J-{data?.declaration_urssaf?.jours_restants} pour déclarer !</span>
              </div>
            )}

            <div className={`p-4 rounded-lg ${data?.declaration_urssaf?.fait ? 'border-2' : 'bg-white/50'}`} style={{borderColor: data?.declaration_urssaf?.fait ? COLORS.success : 'transparent', backgroundColor: data?.declaration_urssaf?.fait ? COLORS.success + '10' : undefined}}>
              {/* Date limite et jours restants */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm text-muted-foreground">Prochaine échéance</div>
                  <div className="text-xl font-bold" style={{color: COLORS.text}}>{data?.declaration_urssaf?.date_limite}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">Reste</div>
                  <div className="text-3xl font-bold" style={{color: data?.declaration_urssaf?.fait ? COLORS.success : data?.declaration_urssaf?.jours_restants <= 7 ? COLORS.error : data?.declaration_urssaf?.jours_restants <= 15 ? COLORS.warning : COLORS.text}}>
                    {data?.declaration_urssaf?.fait ? '✓' : `${data?.declaration_urssaf?.jours_restants}j`}
                  </div>
                </div>
              </div>

              {/* TOTAL À DÉCLARER - en premier */}
              <div className="p-3 rounded-lg mb-3" style={{backgroundColor: COLORS.text + '10', border: `2px solid ${COLORS.text}`}}>
                <div className="flex justify-between items-center">
                  <span className="font-bold" style={{color: COLORS.text}}>TOTAL À DÉCLARER</span>
                  <span className="text-2xl font-bold" style={{color: COLORS.text}}>{formatCurrency(data?.declaration_urssaf?.montant_a_declarer)}</span>
                </div>
                
                {/* Info période à déclarer - MOIS ou TRIMESTRE */}
                {data?.declaration_urssaf?.frequence === "trimestriel" && data?.declaration_urssaf?.trimestre_a_declarer ? (
                  <div className="text-xs text-muted-foreground mt-1">
                    CA de T{data.declaration_urssaf.trimestre_a_declarer} {data.declaration_urssaf.annee_trimestre} ({data.declaration_urssaf.mois_du_trimestre?.join(', ')})
                    {data.declaration_urssaf.en_attente && <span className="ml-2 text-amber-600">(à préparer)</span>}
                  </div>
                ) : data?.declaration_urssaf?.mois_a_declarer && (
                  <div className="text-xs text-muted-foreground mt-1">
                    CA de <span className="font-medium capitalize">{data.declaration_urssaf.mois_a_declarer} {data.declaration_urssaf.annee_mois_a_declarer}</span>
                  </div>
                )}
                
                {/* Détail par activité si double activité */}
                {user?.double_activite && (
                  <div className="mt-2 pt-2 border-t border-dashed space-y-1">
                    <div className="flex justify-between text-sm">
                      <span style={{color: COLORS.principale}}>dont Activité principale</span>
                      <span className="font-medium" style={{color: COLORS.principale}}>{formatCurrency(data?.declaration_urssaf?.montant_a_declarer_principal || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span style={{color: COLORS.accent}}>dont Activité secondaire</span>
                      <span className="font-medium" style={{color: COLORS.accent}}>{formatCurrency(data?.declaration_urssaf?.montant_a_declarer_secondaire || 0)}</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Cotisations du MOIS/TRIMESTRE en cours */}
              <div className="p-3 bg-white/70 rounded-lg space-y-2">
                <div className="text-sm font-medium" style={{color: COLORS.accent}}>Cotisations estimées</div>
                <div className="flex justify-between">
                  <span className="text-sm">Urssaf</span>
                  <span className="font-bold" style={{color: COLORS.principale}}>
                    {formatCurrency(
                      (data?.declaration_urssaf?.cotisations_detail_principal?.cotisations_urssaf || 0) +
                      (data?.declaration_urssaf?.cotisations_detail_secondaire?.cotisations_urssaf || 0)
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">CFP</span>
                  <span className="font-medium">
                    {formatCurrency(
                      (data?.declaration_urssaf?.cotisations_detail_principal?.cfp || 0) +
                      (data?.declaration_urssaf?.cotisations_detail_secondaire?.cfp || 0)
                    )}
                  </span>
                </div>
                {(data?.declaration_urssaf?.vl_periode > 0 || data?.cotisations?.vl_mois > 0) && (
                  <div className="flex justify-between">
                    <span className="text-sm">Versement libératoire</span>
                    <span className="font-bold" style={{color: COLORS.accent}}>{formatCurrency(data?.declaration_urssaf?.vl_periode || data?.cotisations?.vl_mois)}</span>
                  </div>
                )}
                <hr className="border-dashed" />
                <div className="flex justify-between">
                  <span className="font-medium">Total cotisations</span>
                  <span className="font-bold text-lg" style={{color: COLORS.text}}>{formatCurrency(data?.declaration_urssaf?.prelevement_estime)}</span>
                </div>
                {data?.profil?.acre_actif && (
                  <div className="p-2 bg-emerald-100 rounded text-xs text-emerald-700 flex items-center gap-1">
                    <Check size={14} /> ACRE actif : -50%
                  </div>
                )}
                {/* Info prélèvement */}
                {data?.declaration_urssaf?.prelevement_info && (
                  <div className="p-2 rounded text-xs flex items-center gap-1" style={{backgroundColor: COLORS.bg, color: COLORS.accent}}>
                    <Clock size={14} /> Prochain prélèvement Urssaf
                    <span className="ml-auto font-medium">{data.declaration_urssaf.prelevement_info}</span>
                  </div>
                )}
              </div>
              
              {/* Toggle style iPhone pour marquer comme faite */}
              <div className="mt-4 flex items-center justify-between p-3 rounded-lg" style={{backgroundColor: COLORS.secondaire + '30'}}>
                <span className="text-sm font-medium" style={{color: COLORS.text}}>Déclaration effectuée</span>
                <button 
                  onClick={handleMarkDeclarationDone}
                  className="relative w-14 h-8 rounded-full transition-colors duration-300"
                  style={{backgroundColor: data?.declaration_urssaf?.fait ? COLORS.success : '#D1D5DB'}}
                  data-testid="dashboard-toggle-declaration-faite"
                >
                  <span className={`absolute left-0 top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${data?.declaration_urssaf?.fait ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* ==================== INTÉGRATION API URSSAF TDAE ====================
                  Pilote fermé : seuls les comptes bêta (Tiphaine + Paul) ont accès
                  en PROD. Les autres AE voient une bannière "coming soon".
                  En sandbox/mock, visible uniquement à l'admin pour les tests. */}
              {(() => {
                const URSSAF_BETA_EMAILS = ["t.samblat@gmail.com", "paulttfrouzins@gmail.com"];
                const isBeta = user?.email && URSSAF_BETA_EMAILS.includes(user.email.toLowerCase());
                // Teaser : visible à tous les non-bêta (prod ET preview) pour cohérence
                const showTeasing = urssafStatus && !isBeta;
                // UI complète : uniquement bêta (toute env confondue)
                const showFullUi = urssafStatus && isBeta;
                if (showTeasing) {
                  return (
                    <div
                      className="mt-4 p-4 rounded-lg flex items-start gap-3"
                      style={{backgroundColor: COLORS.bg, border: `2px dashed ${COLORS.accent}`, color: COLORS.text}}
                      data-testid="urssaf-teasing-banner"
                    >
                      <ShieldCheck size={22} style={{color: COLORS.accent, flexShrink: 0, marginTop: 2}} />
                      <div className="text-sm space-y-1">
                        <div className="font-bold" style={{color: COLORS.accent}}>Déclaration Urssaf en 1 clic — bientôt disponible 🚀</div>
                        <div>
                          L'intégration officielle avec l'API Urssaf (TDAE) est en phase finale de certification.
                          Dès sa mise en service, tu pourras déclarer ton CA et payer tes cotisations directement
                          depuis Biz360, sans jamais quitter l'application.
                          <br/><strong>Tu seras prévenu(e) par email dès l'ouverture.</strong>
                        </div>
                      </div>
                    </div>
                  );
                }
                if (!showFullUi) return null;
                return (
              <div className="mt-4 space-y-2">
                {!urssafStatus?.mandat ? (
                  <button
                    onClick={() => setShowUrssafSetup(true)}
                    className="w-full py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                    style={{backgroundColor: COLORS.accent}}
                    data-testid="urssaf-connect-btn"
                  >
                    <ShieldCheck size={18} /> Connecter mon compte Urssaf
                  </button>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-xs p-2 rounded-lg" style={{backgroundColor: COLORS.success + '15', color: COLORS.success}}>
                      <Check size={14} />
                      <span className="flex-1">
                        Compte Urssaf connecté · SIRET {urssafStatus.mandat.siret?.slice(0,3)} {urssafStatus.mandat.siret?.slice(3,6)} {urssafStatus.mandat.siret?.slice(6,9)} {urssafStatus.mandat.siret?.slice(9)}
                      </span>
                      {urssafStatus.env !== "production" && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{backgroundColor: '#FEF3C7', color: '#92400E'}}>
                          {urssafStatus.env === "sandbox" ? "SANDBOX" : "DÉMO"}
                        </span>
                      )}
                    </div>

                    {urssafStatus?.mandat?.periodicite && urssafStatus?.periode_statut?.periodicite && (
                      (urssafStatus.mandat.periodicite !== urssafStatus.periode_statut.periodicite) && (
                        <div
                          className="p-3 rounded-lg text-xs space-y-1"
                          style={{backgroundColor: COLORS.bg, border: `2px solid ${COLORS.error}`, color: COLORS.text}}
                          data-testid="urssaf-periodicity-mismatch-encart"
                        >
                          <div className="font-bold" style={{color: COLORS.error}}>
                            ⚠ Périodicité incohérente avec l'Urssaf
                          </div>
                          <div>
                            Ton mandat Urssaf : <strong>{urssafStatus.mandat.periodicite === "T" ? "trimestriel" : "mensuel"}</strong>
                            {" · "}Ton réglage Biz360 : <strong>{urssafStatus.periode_statut.periodicite === "T" ? "trimestriel" : "mensuel"}</strong>.
                            Corrige la fréquence dans <a href="/parametres" style={{color: COLORS.accent, textDecoration: "underline"}}>Paramètres</a> ou reconnecte ton compte Urssaf.
                          </div>
                        </div>
                      )
                    )}

                    {urssafStatus?.periode_statut && !urssafStatus.periode_statut.ouverte ? (
                      <button
                        onClick={() => setShowUrssafDeclare(true)}
                        className="w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                        style={{
                          backgroundColor: COLORS.bg,
                          color: urssafStatus.periode_statut.statut === "cloturee" ? COLORS.error : COLORS.accent,
                          border: `2px solid ${urssafStatus.periode_statut.statut === "cloturee" ? COLORS.error : COLORS.accent}`,
                        }}
                        data-testid="urssaf-declarer-btn"
                        title="La période ouvrira à cette date"
                      >
                        <Clock size={16} />
                        {urssafStatus.periode_statut.statut === "future"
                          ? `Ouverture le ${new Date(urssafStatus.periode_statut.date_ouverture).toLocaleDateString("fr-FR")}`
                          : `Période clôturée le ${new Date(urssafStatus.periode_statut.date_limite).toLocaleDateString("fr-FR")}`}
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowUrssafDeclare(true)}
                        className="w-full py-3 rounded-lg text-white font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                        style={{backgroundColor: COLORS.success}}
                        data-testid="urssaf-declarer-btn"
                      >
                        <Send size={18} />
                        Déclarer {urssafStatus?.mandat?.periodicite === "M" ? "le mois" : "le trimestre"} en 1 clic
                      </button>
                    )}

                    <button
                      onClick={() => setShowUrssafHistory(!showUrssafHistory)}
                      className="w-full py-2 rounded-lg text-sm border flex items-center justify-center gap-2 hover:bg-gray-50"
                      style={{color: COLORS.accent, borderColor: COLORS.accent}}
                      data-testid="urssaf-history-btn"
                    >
                      <History size={16} /> Historique ({urssafHistory.length})
                    </button>

                    {showUrssafHistory && urssafHistory.length > 0 && (
                      <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border" data-testid="urssaf-history-list">
                        {urssafHistory.map((d) => {
                          const isRemplacee = d.statut === "remplacee";
                          const isRejetee = d.statut === "rejetee";
                          const isRectif = d.is_rectificative;
                          const isPayee = d.paiement_effectue;
                          // Formate date_prelevement YYYY-MM-DD → JJ/MM
                          const prelevDate = d.date_prelevement
                            ? (() => { const [y,m,day] = d.date_prelevement.split("-"); return `${day}/${m}`; })()
                            : null;
                          const badge = isRemplacee
                            ? { label: "Remplacée", bg: "#F3F4F6", color: "#6B7280" }
                            : isRejetee
                              ? { label: "Rejetée", bg: "#FEE2E2", color: "#991B1B" }
                              : isPayee
                                ? { label: "✓ Payée", bg: COLORS.success + "20", color: COLORS.success }
                                : prelevDate
                                  ? { label: `Prélèvement ${prelevDate}`, bg: COLORS.accent + "20", color: COLORS.accent }
                                  : { label: "Déclarée ✓", bg: COLORS.success + "20", color: COLORS.success };
                          const numeroShort = d.numero_declaration
                            ? (d.numero_declaration.length > 20
                                ? d.numero_declaration.slice(-10)
                                : d.numero_declaration)
                            : "—";
                          return (
                            <div
                              key={d.id}
                              className="p-2.5 border-b last:border-0 text-xs"
                              style={{
                                borderColor: '#f3f4f6',
                                opacity: isRemplacee ? 0.55 : 1,
                                textDecoration: isRemplacee ? "line-through" : "none",
                              }}
                            >
                              <div className="flex justify-between items-start gap-2">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-medium" style={{color: COLORS.text}}>{d.periode}</span>
                                    {isRectif && !isRemplacee && (
                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{backgroundColor: COLORS.accent + "20", color: COLORS.accent}}>
                                        Rectificative
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-muted-foreground font-mono text-[10px] mt-0.5" title={d.numero_declaration}>
                                    {numeroShort}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <div className="font-bold" style={{color: isRemplacee ? "#9CA3AF" : COLORS.accent}}>
                                    {formatCurrency(d.montant_a_payer)}
                                  </div>
                                  <span
                                    className="inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold mt-0.5"
                                    style={{backgroundColor: badge.bg, color: badge.color}}
                                  >
                                    {badge.label}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Modales Urssaf TDAE */}
        <UrssafSetupModal
          open={showUrssafSetup}
          onClose={() => setShowUrssafSetup(false)}
          onSuccess={() => { loadUrssafStatus(); loadDashboard(); }}
          initialSiret={user?.siret || ""}
          initialPeriodicite={user?.urssaf_periodicite || (user?.declaration_frequence === "mensuel" ? "M" : "T")}
        />
        <UrssafDeclareModal
          open={showUrssafDeclare}
          onClose={() => setShowUrssafDeclare(false)}
          onSuccess={() => { loadHistory(); loadDashboard(); }}
          dashboardSnapshot={data?.declaration_urssaf}
        />

        {/* Message contextuel démo */}
        <DemoContextMessage type="dashboard" />

        <SimulateurShortcut />

        {/* SECTIONS RÉORGANISÉES */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          
          {/* 3. MES SEUILS */}
          <div className="rounded-xl border p-6" style={{backgroundColor: COLORS.bg}}>
            <h3 className="font-semibold mb-4" style={{color: COLORS.accent}}>MES SEUILS</h3>
            
            {/* Activité principale */}
            <div className="text-sm font-medium mb-3" style={{color: COLORS.principale}}>Activité principale</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-muted-foreground">CA Max</div>
                <div className="font-bold" style={{color: COLORS.text}}>{formatCurrency(getPlafond(user?.type_activite))}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Type bénéfices</div>
                <div className="font-bold" style={{color: COLORS.text}}>{getBeneficeType(user?.type_activite)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Abattement</div>
                <div className="font-bold" style={{color: COLORS.text}}>{getAbattement(user?.type_activite, user?.activite)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Seuil TVA base</div>
                <div className="font-bold" style={{color: COLORS.text}}>{formatCurrency(getSeuilTVA(user?.type_activite).base)}</div>
              </div>
            </div>
            
            {/* Activité secondaire - même disposition */}
            {user?.double_activite && (
              <div className="mt-4 pt-4 border-t" style={{borderColor: COLORS.accent + '40'}}>
                <div className="text-sm font-medium mb-3" style={{color: COLORS.accent}}>Activité secondaire</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground">CA Max</div>
                    <div className="font-bold" style={{color: COLORS.text}}>{formatCurrency(getPlafond(user?.type_activite_secondaire))}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Type bénéfices</div>
                    <div className="font-bold" style={{color: COLORS.text}}>{getBeneficeType(user?.type_activite_secondaire)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Abattement</div>
                    <div className="font-bold" style={{color: COLORS.text}}>{getAbattement(user?.type_activite_secondaire, user?.activite_secondaire)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Seuil TVA base</div>
                    <div className="font-bold" style={{color: COLORS.text}}>{formatCurrency(getSeuilTVA(user?.type_activite_secondaire).base)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 4. MES SEUILS TVA / MA TVA */}
          <div className="rounded-xl border p-6" style={{backgroundColor: COLORS.bg}}>
            <h3 className="font-semibold mb-4" style={{color: COLORS.accent}}>
              {data?.statut_tva?.assujetti ? "MA TVA" : "MES SEUILS TVA"}
            </h3>
            
            {/* CAS 1 : Assujetti TVA - Afficher le calcul de TVA + régime + échéances */}
            {data?.statut_tva?.assujetti ? (
              <div>
                {/* Bandeau statut + régime */}
                <div className="p-3 rounded-lg mb-4" style={{backgroundColor: COLORS.accent + '15'}}>
                  <div className="text-sm text-center" style={{color: COLORS.accent}}>
                    📋 Redevable depuis le <strong>{data.statut_tva.assujetti_depuis}</strong>
                  </div>
                  {data?.tva_data?.echeances && (
                    <div className="mt-2 pt-2 border-t border-dashed flex items-center justify-between text-xs">
                      <span>Régime : <strong>{data.tva_data.echeances.regime_label}</strong></span>
                      <span className="px-2 py-0.5 rounded" style={{backgroundColor: COLORS.principale + '20', color: COLORS.principale}}>
                        {data.tva_data.echeances.formulaire}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Prochaine échéance TVA */}
                {data?.tva_data?.echeances?.prochaine_echeance && (
                  <div className={`p-3 rounded-lg mb-4 ${data.tva_data.echeances.prochaine_echeance.urgent ? 'animate-pulse' : ''}`} 
                       style={{
                         backgroundColor: data.tva_data.echeances.prochaine_echeance.urgent ? COLORS.error + '20' : COLORS.warning + '10',
                         border: `2px solid ${data.tva_data.echeances.prochaine_echeance.urgent ? COLORS.error : COLORS.warning}`
                       }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-muted-foreground">Prochaine échéance</div>
                        <div className="font-bold" style={{color: data.tva_data.echeances.prochaine_echeance.urgent ? COLORS.error : COLORS.text}}>
                          {data.tva_data.echeances.prochaine_echeance.type === 'acompte_juillet' && '📅 Acompte juillet'}
                          {data.tva_data.echeances.prochaine_echeance.type === 'acompte_decembre' && '📅 Acompte décembre'}
                          {data.tva_data.echeances.prochaine_echeance.type === 'ca12' && '📋 Déclaration CA12'}
                          {data.tva_data.echeances.prochaine_echeance.type === 'ca3_mensuel' && `📋 CA3 - ${data.tva_data.echeances.prochaine_echeance.periode}`}
                          {data.tva_data.echeances.prochaine_echeance.type === 'ca3_trimestriel' && `📋 CA3 - ${data.tva_data.echeances.prochaine_echeance.periode}`}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold" style={{color: data.tva_data.echeances.prochaine_echeance.urgent ? COLORS.error : COLORS.warning}}>
                          J-{data.tva_data.echeances.prochaine_echeance.jours_restants}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {data.tva_data.echeances.prochaine_echeance.date_limite}
                        </div>
                      </div>
                    </div>
                    {data.tva_data.echeances.prochaine_echeance.montant > 0 && (
                      <div className="mt-2 pt-2 border-t border-dashed flex justify-between items-center">
                        <span className="text-sm">Montant estimé</span>
                        <span className="font-bold" style={{color: COLORS.error}}>
                          {formatCurrency(data.tva_data.echeances.prochaine_echeance.montant)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Message dispense acomptes : TVA N-1 < 1000€ OU 1ère année d'assujettissement */}
                {data?.tva_data?.echeances?.dispense_acomptes && (
                  <div
                    className="p-3 rounded-lg mb-3 text-xs"
                    style={{
                      backgroundColor: data.tva_data.echeances.premiere_annee
                        ? COLORS.warning + '15'
                        : COLORS.success + '15',
                      color: data.tva_data.echeances.premiere_annee ? COLORS.warning : COLORS.success,
                      border: `1px solid ${data.tva_data.echeances.premiere_annee ? COLORS.warning : COLORS.success}40`,
                    }}
                    data-testid="tva-dispense-acomptes"
                  >
                    {data.tva_data.echeances.premiere_annee ? '🆕 ' : '✅ '}
                    {data.tva_data.echeances.message_dispense}
                  </div>
                )}
                
                {/* Calcul TVA */}
                {data?.tva_data && (
                  <div className="space-y-3">
                    {/* TVA Collectée */}
                    <div className="flex justify-between items-center p-3 rounded-lg" style={{backgroundColor: COLORS.warning + '15'}}>
                      <div>
                        <div className="text-sm font-medium">TVA collectée</div>
                        <div className="text-xs text-muted-foreground">Sur tes ventes</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold" style={{color: COLORS.warning}}>
                          {formatCurrency(data.tva_data.tva_collectee_annee || 0)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          dont {formatCurrency(data.tva_data.tva_collectee_mois || 0)} ce mois
                        </div>
                      </div>
                    </div>
                    
                    {/* TVA Déductible */}
                    <div className="flex justify-between items-center p-3 rounded-lg" style={{backgroundColor: COLORS.success + '15'}}>
                      <div>
                        <div className="text-sm font-medium">TVA déductible</div>
                        <div className="text-xs text-muted-foreground">Sur tes achats</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold" style={{color: COLORS.success}}>
                          - {formatCurrency(data.tva_data.tva_deductible_annee || 0)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          dont {formatCurrency(data.tva_data.tva_deductible_mois || 0)} ce mois
                        </div>
                      </div>
                    </div>
                    
                    {/* Séparateur */}
                    <div className="border-t border-dashed my-2"></div>
                    
                    {/* TVA Nette à reverser */}
                    <div className="flex justify-between items-center p-4 rounded-lg" style={{
                      backgroundColor: data.tva_data.tva_nette_annee >= 0 ? COLORS.error + '20' : COLORS.success + '20',
                      border: `2px solid ${data.tva_data.tva_nette_annee >= 0 ? COLORS.error : COLORS.success}`
                    }}>
                      <div>
                        <div className="font-medium">
                          {data.tva_data.tva_nette_annee >= 0 ? "TVA à reverser" : "Crédit de TVA"}
                        </div>
                        <div className="text-xs text-muted-foreground">Année {new Date().getFullYear()}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold" style={{
                          color: data.tva_data.tva_nette_annee >= 0 ? COLORS.error : COLORS.success
                        }}>
                          {formatCurrency(Math.abs(data.tva_data.tva_nette_annee || 0))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Récapitulatif échéances RSI (si régime simplifié ET base TVA N-1 disponible) */}
                    {data.tva_data.echeances?.regime === 'simplifie' && !data.tva_data.echeances?.dispense_acomptes && (data.tva_data.tva_nette_n_moins_1 || 0) > 0 && (
                      <div className="mt-3 p-3 rounded-lg text-xs" style={{backgroundColor: COLORS.bg, border: '1px solid #e5e7eb'}}>
                        <div className="font-medium mb-2" style={{color: COLORS.accent}}>📆 Calendrier TVA {new Date().getFullYear()}</div>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span>Acompte juillet (55%)</span>
                            <span className="font-medium">{formatCurrency(data.tva_data.echeances.acompte_juillet?.montant || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Acompte décembre (40%)</span>
                            <span className="font-medium">{formatCurrency(data.tva_data.echeances.acompte_decembre?.montant || 0)}</span>
                          </div>
                          <div className="flex justify-between pt-1 border-t border-dashed">
                            <span>Solde CA12 (mai {new Date().getFullYear() + 1})</span>
                            <span className="text-muted-foreground">à calculer</span>
                          </div>
                        </div>
                        <div className="mt-2 text-muted-foreground text-center">
                          Base : TVA N-1 = {formatCurrency(data.tva_data.tva_nette_n_moins_1 || 0)}
                          {data.tva_data.tva_nette_n_moins_1_source === "estimation_1ere_annee" && (
                            <span className="ml-1 italic">(estimation 1ère année)</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Info pour les users en RSI sans base N-1 (= pas encore d'historique TVA, hors 1ère année qui a déjà son propre message) */}
                    {data.tva_data.echeances?.regime === 'simplifie' && (data.tva_data.tva_nette_n_moins_1 || 0) === 0 && !data.tva_data.echeances?.premiere_annee && (
                      <div className="mt-3 p-3 rounded-lg text-xs bg-amber-50 border border-amber-200">
                        <div className="font-medium mb-1 text-amber-800">📆 Acomptes TVA — pas encore calculables</div>
                        <div className="text-amber-700 leading-snug">
                          Tu n'as pas d'historique TVA pour {new Date().getFullYear() - 1}. Les acomptes RSI (juillet 55% + décembre 40%) seront calculés une fois que tu auras déclaré ta première CA12 (déclaration annuelle). Pour l'instant, tu paies ta TVA en une seule fois à la CA12 de mai prochain.
                        </div>
                      </div>
                    )}
                    
                    {/* Info */}
                    <div className="text-xs text-muted-foreground text-center mt-2">
                      {data.tva_data.tva_collectee_annee === 0 && (
                        <span>💡 Ajoute tes ventes avec TVA pour voir la TVA collectée</span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Info activité mixte */}
                {data.statut_tva.is_activite_mixte && (
                  <div className="text-xs p-2 rounded mt-3" style={{backgroundColor: COLORS.bg, border: '1px solid #e5e7eb'}}>
                    ℹ️ <strong>Activité mixte :</strong> TVA sur toutes tes activités (ventes ET prestations).
                  </div>
                )}
                
                {/* Lien changer de régime */}
                <div className="mt-4 pt-3 border-t border-dashed">
                  <button 
                    onClick={() => navigate('/parametres')}
                    className="w-full text-xs text-center py-2 rounded-lg hover:opacity-80 transition"
                    style={{backgroundColor: COLORS.secondaire, color: COLORS.accent}}
                  >
                    ⚙️ Changer de régime TVA
                  </button>
                </div>
              </div>
            
            /* CAS 2 : Zone de tolérance */
            ) : data?.statut_tva?.zone_tolerance ? (
              <div className="p-4 rounded-lg" style={{backgroundColor: COLORS.warning + '20', border: `2px solid ${COLORS.warning}`}}>
                <div className="text-center mb-3">
                  <div className="text-2xl mb-2">⚠️</div>
                  <div className="font-bold" style={{color: COLORS.warning}}>Zone de tolérance TVA</div>
                  <div className="text-sm text-muted-foreground mt-2">
                    {data.statut_tva.seuil_declenche === 'base_services' 
                      ? `CA prestations (${formatCurrency(data.statut_tva.ca_services)}) > seuil base services (${formatCurrency(data.statut_tva.seuil_base_services)})`
                      : data.statut_tva.seuil_declenche === 'base_global'
                        ? `CA global (${formatCurrency(data.statut_tva.ca_global)}) > seuil base global (${formatCurrency(data.statut_tva.seuil_base_global)})`
                        : `CA (${formatCurrency(data.statut_tva.ca_global)}) > seuil base (${formatCurrency(data.statut_tva.seuil_base_global)})`
                    }
                  </div>
                </div>
                <div className="text-xs p-2 rounded text-center" style={{backgroundColor: COLORS.bg}}>
                  Franchise maintenue jusqu'au 31/12/{new Date().getFullYear()}.<br/>
                  <strong style={{color: COLORS.error}}>TVA obligatoire sur TOUT ton CA dès le 1er janvier {new Date().getFullYear() + 1}</strong>
                </div>
                {/* Jauge vers le seuil majoré */}
                <div className="mt-4">
                  <div className="text-xs mb-2 text-center text-muted-foreground">
                    Surveillance du seuil majoré (dépassé = TVA immédiate)
                  </div>
                  <div className="h-3 rounded-full overflow-hidden" style={{backgroundColor: '#E5E7EB'}}>
                    <div className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${Math.min(100, (data.statut_tva.ca_global / data.statut_tva.seuil_majore_global) * 100)}%`,
                        backgroundColor: (data.statut_tva.ca_global / data.statut_tva.seuil_majore_global) * 100 >= 90 ? COLORS.error : COLORS.warning
                      }}
                    />
                  </div>
                  <div className="text-xs mt-1 text-center">
                    {formatCurrency(data.statut_tva.ca_global)} / {formatCurrency(data.statut_tva.seuil_majore_global)}
                  </div>
                </div>
              </div>
            
            /* CAS 3 : Prorata création en cours d'année avec alerte N+1 */
            ) : data?.statut_tva?.creation_en_cours_annee && data?.statut_tva?.assujetti_au_1er_jan_n1 ? (
              <div className="p-4 rounded-lg" style={{backgroundColor: COLORS.warning + '20', border: `2px solid ${COLORS.warning}`}}>
                <div className="text-center mb-3">
                  <div className="text-2xl mb-2">📊</div>
                  <div className="font-bold" style={{color: COLORS.warning}}>Attention : TVA en {new Date().getFullYear() + 1}</div>
                  <div className="text-sm text-muted-foreground mt-2">
                    Année de création ({data.statut_tva.jours_activite} jours d'activité)
                  </div>
                </div>
                <div className="text-xs p-2 rounded" style={{backgroundColor: COLORS.bg}}>
                  <div className="mb-2">
                    <strong>CA proratisé :</strong> {formatCurrency(data.statut_tva.ca_proratise)} 
                    <span className="text-muted-foreground"> (CA réel × 365 / {data.statut_tva.jours_activite} jours)</span>
                  </div>
                  <div style={{color: COLORS.error}}>
                    → Dépasse le seuil de base → <strong>TVA obligatoire dès le 1er janvier {new Date().getFullYear() + 1}</strong>
                  </div>
                </div>
              </div>
            
            /* CAS 4 : En franchise */
            ) : (
              <div className="space-y-4">
                {/* Activité SIMPLE */}
                {!data?.statut_tva?.is_activite_mixte && (
                  <div>
                    <div className="flex justify-between text-xs mb-2 px-1">
                      <span className="px-2 py-0.5 rounded" style={{backgroundColor: COLORS.secondaire}}>Base: {formatCurrency(data?.statut_tva?.seuil_base_global)}</span>
                      <span className="px-2 py-0.5 rounded" style={{backgroundColor: COLORS.warning + '30'}}>Majoré: {formatCurrency(data?.statut_tva?.seuil_majore_global)}</span>
                    </div>
                    <div className="h-5 rounded-full overflow-hidden" style={{backgroundColor: '#E5E7EB'}}>
                      <div className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${Math.min(100, (data?.statut_tva?.ca_global / data?.statut_tva?.seuil_base_global) * 100 || 0)}%`,
                          backgroundColor: (data?.statut_tva?.ca_global / data?.statut_tva?.seuil_base_global) * 100 >= 90 ? COLORS.error 
                            : (data?.statut_tva?.ca_global / data?.statut_tva?.seuil_base_global) * 100 >= 70 ? COLORS.warning 
                            : COLORS.principale
                        }}
                      />
                    </div>
                    <div className="text-sm mt-2 text-center font-medium" style={{color: COLORS.text}}>
                      {formatCurrency(data?.statut_tva?.ca_global)} / {formatCurrency(data?.statut_tva?.seuil_base_global)} 
                      ({((data?.statut_tva?.ca_global / data?.statut_tva?.seuil_base_global) * 100 || 0).toFixed(0)}%)
                    </div>
                    {/* Prorata si création en cours d'année */}
                    {data?.statut_tva?.creation_en_cours_annee && data?.statut_tva?.ca_proratise && (
                      <div className="mt-2 text-xs p-2 rounded" style={{backgroundColor: COLORS.secondaire}}>
                        <strong>Prorata :</strong> {formatCurrency(data.statut_tva.ca_proratise)} 
                        <span className="text-muted-foreground"> ({data.statut_tva.jours_activite}j d'activité)</span>
                        {data.statut_tva.ca_proratise <= data.statut_tva.seuil_base_global && (
                          <span className="ml-2" style={{color: COLORS.success}}>✓ OK pour {new Date().getFullYear() + 1}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Activité MIXTE */}
                {data?.statut_tva?.is_activite_mixte && (
                  <div>
                    {/* Encart explicatif */}
                    <div className="text-xs p-2 rounded mb-3" style={{backgroundColor: COLORS.warning + '15'}}>
                      <strong>Activité mixte :</strong> Plafond global {formatCurrency(data.statut_tva.seuil_base_global)} dont max {formatCurrency(data.statut_tva.seuil_base_services)} en prestations.
                    </div>
                    
                    {/* Jauge CA Global */}
                    <div className="mb-4">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium">CA Global</span>
                        <span className="text-muted-foreground">Base: {formatCurrency(data.statut_tva.seuil_base_global)} | Majoré: {formatCurrency(data.statut_tva.seuil_majore_global)}</span>
                      </div>
                      <div className="h-4 rounded-full overflow-hidden" style={{backgroundColor: '#E5E7EB'}}>
                        <div className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${Math.min(100, (data.statut_tva.ca_global / data.statut_tva.seuil_base_global) * 100)}%`,
                            backgroundColor: (data.statut_tva.ca_global / data.statut_tva.seuil_base_global) * 100 >= 90 ? COLORS.error 
                              : (data.statut_tva.ca_global / data.statut_tva.seuil_base_global) * 100 >= 70 ? COLORS.warning 
                              : '#374151'
                          }}
                        />
                      </div>
                      <div className="text-xs mt-1 text-center" style={{color: COLORS.text}}>
                        {formatCurrency(data.statut_tva.ca_global)} ({((data.statut_tva.ca_global / data.statut_tva.seuil_base_global) * 100).toFixed(0)}%)
                      </div>
                    </div>
                    
                    {/* Jauge CA Services (sous-plafond) */}
                    <div className="mb-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium" style={{color: COLORS.accent}}>Dont Prestations</span>
                        <span className="text-muted-foreground">Max: {formatCurrency(data.statut_tva.seuil_base_services)}</span>
                      </div>
                      <div className="h-3 rounded-full overflow-hidden" style={{backgroundColor: '#E5E7EB'}}>
                        <div className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${Math.min(100, (data.statut_tva.ca_services / data.statut_tva.seuil_base_services) * 100)}%`,
                            backgroundColor: (data.statut_tva.ca_services / data.statut_tva.seuil_base_services) * 100 >= 90 ? COLORS.error 
                              : (data.statut_tva.ca_services / data.statut_tva.seuil_base_services) * 100 >= 70 ? COLORS.warning 
                              : COLORS.accent
                          }}
                        />
                      </div>
                      <div className="text-xs mt-1 text-center text-muted-foreground">
                        {formatCurrency(data.statut_tva.ca_services)} ({((data.statut_tva.ca_services / data.statut_tva.seuil_base_services) * 100).toFixed(0)}%)
                      </div>
                    </div>
                    
                    {/* Jauge CA Ventes (sous-plafond ventes) */}
                    <div className="mb-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium" style={{color: COLORS.principale}}>Dont Ventes</span>
                        <span className="text-muted-foreground">Max: {formatCurrency(188700)}</span>
                      </div>
                      <div className="h-3 rounded-full overflow-hidden" style={{backgroundColor: '#E5E7EB'}}>
                        <div className="h-full rounded-full transition-all"
                          style={{ 
                            width: `${Math.min(100, (data.statut_tva.ca_ventes / 188700) * 100)}%`,
                            backgroundColor: (data.statut_tva.ca_ventes / 188700) * 100 >= 90 ? COLORS.error 
                              : (data.statut_tva.ca_ventes / 188700) * 100 >= 70 ? COLORS.warning 
                              : COLORS.principale
                          }}
                        />
                      </div>
                      <div className="text-xs mt-1 text-center text-muted-foreground">
                        {formatCurrency(data.statut_tva.ca_ventes)} ({((data.statut_tva.ca_ventes / 188700) * 100).toFixed(0)}%)
                      </div>
                    </div>
                    
                    {/* Prorata si création en cours d'année */}
                    {data?.statut_tva?.creation_en_cours_annee && data?.statut_tva?.ca_proratise && (
                      <div className="mt-3 text-xs p-2 rounded" style={{backgroundColor: COLORS.secondaire}}>
                        <strong>Prorata ({data.statut_tva.jours_activite}j) :</strong> {formatCurrency(data.statut_tva.ca_proratise)}
                        {!data.statut_tva.assujetti_au_1er_jan_n1 && (
                          <span className="ml-2" style={{color: COLORS.success}}>✓ OK pour {new Date().getFullYear() + 1}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 5. Graphique CA sur l'année (courbe) */}
          <div className="rounded-xl border p-4 col-span-1 md:col-span-2 lg:col-span-3" style={{backgroundColor: COLORS.bg}}>
            <h3 className="font-semibold mb-3 text-sm" style={{color: COLORS.accent}}>MON CA SUR L'ANNÉE</h3>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={caLineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.accent + '30'} />
                  <XAxis dataKey="mois" tick={{fontSize: 10}} stroke={COLORS.text} />
                  <YAxis tick={{fontSize: 10}} stroke={COLORS.text} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Line type="monotone" dataKey="principal" stroke={COLORS.principale} strokeWidth={2} dot={{fill: COLORS.principale, r: 3}} name="Principale" />
                  {user?.double_activite && (
                    <Line type="monotone" dataKey="secondaire" stroke={COLORS.accent} strokeWidth={2} dot={{fill: COLORS.accent, r: 3}} name="Secondaire" />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2 text-xs">
              <div className="flex items-center gap-1"><div className="w-3 h-1 rounded" style={{backgroundColor: COLORS.principale}}></div> Principale</div>
              {user?.double_activite && <div className="flex items-center gap-1"><div className="w-3 h-1 rounded" style={{backgroundColor: COLORS.accent}}></div> Secondaire</div>}
            </div>
          </div>

          {/* MON RÉSULTAT a été remonté en Section 1 (3 cols top) — plus affiché ici */}

          {/* 7. MES DÉPENSES */}
          <div className="rounded-xl border p-6" style={{backgroundColor: COLORS.bg}}>
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{color: COLORS.accent}}>
              <Wallet size={20} /> MES DÉPENSES
            </h3>
            <div className="space-y-3">
              {/* Reste à payer ce mois - avec fond de la charte */}
              <div className="p-3 rounded-lg" style={{backgroundColor: COLORS.warning + '15', border: `1px solid ${COLORS.warning}30`}}>
                <div className="text-sm text-muted-foreground mb-1">Reste à payer ce mois</div>
                <div className="text-xl font-bold" style={{color: COLORS.error}}>{formatCurrency(data?.resume?.depenses_a_payer_total || 0)}</div>
                {data?.depenses_a_payer?.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
                    {data.depenses_a_payer.slice(0, 4).map((d, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{d.nature} {d.recurrence === 'abonnement' ? '🔄' : ''}</span>
                        <span>{formatCurrency(d.montant)}</span>
                      </div>
                    ))}
                    {data.depenses_a_payer.length > 4 && <div className="text-center">...</div>}
                  </div>
                )}
              </div>
              
              {/* Prévu M+1 */}
              {data?.resume?.depenses_m_plus_1 > 0 && (
                <div className="p-3 rounded-lg" style={{backgroundColor: COLORS.accent + '10', border: `1px solid ${COLORS.accent}20`}}>
                  <div className="text-sm text-muted-foreground mb-1">Prévu M+1</div>
                  <div className="font-bold" style={{color: COLORS.accent}}>{formatCurrency(data?.resume?.depenses_m_plus_1)}</div>
                  {data?.depenses_m_plus_1_details?.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
                      {data.depenses_m_plus_1_details.slice(0, 4).map((d, i) => (
                        <div key={i} className="flex justify-between">
                          <span>{d.nature} {d.type === 'abonnement' ? '🔄' : ''}</span>
                          <span>{formatCurrency(d.montant)}</span>
                        </div>
                      ))}
                      {data.depenses_m_plus_1_details.length > 4 && <div className="text-center">...</div>}
                    </div>
                  )}
                </div>
              )}
              
              <button 
                onClick={() => navigate('/depenses')}
                className="w-full p-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:opacity-80 transition"
                style={{backgroundColor: COLORS.accent, color: 'white'}}
              >
                Voir tout <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* 8. MA CFE — toujours affichée. L'alerte nov-déc n'apparaît que si redevable. */}
          <div className="rounded-xl border p-6" style={{backgroundColor: COLORS.bg, borderLeft: `4px solid ${COLORS.accent}`}} data-testid="dashboard-cfe-card">
            <h3 className="font-semibold mb-4" style={{color: COLORS.accent}}>MA CFE</h3>
            {/* Alerte échéance CFE : visible en novembre + décembre, uniquement si redevable (pas d'alerte pour les exonérés) */}
            {data?.cfe?.redevable && (() => {
              const m = new Date().getMonth() + 1;
              if (m !== 11 && m !== 12) return null;
              return (
                <div className="mb-4 p-3 rounded-lg flex items-start gap-2 text-xs" style={{backgroundColor: COLORS.warning + '20', border: `1px solid ${COLORS.warning}`, color: '#92400E'}} data-testid="cfe-echeance-alert">
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Échéance CFE proche</strong> — pense à vérifier ton <a href="https://www.impots.gouv.fr/professionnel" target="_blank" rel="noopener noreferrer" className="underline font-semibold">espace impots.gouv.fr pro</a> pour télécharger ton avis et régler avant le 15 décembre.
                  </div>
                </div>
              );
            })()}
            {data?.cfe?.redevable ? (
              <div>
                <div className="text-center mb-4">
                  <div className="text-lg font-bold" style={{color: COLORS.text}}>{data?.cfe?.raison}</div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-3 rounded-lg" style={{backgroundColor: COLORS.warning + '15'}}>
                    <span>Estimation</span>
                    <div className="text-right">
                      <div className="text-xl font-bold" style={{color: COLORS.warning}}>
                        ≈ {formatCurrency(data?.cfe?.estimation?.montant_estime || 0)}
                      </div>
                      {data?.cfe?.estimation?.montant_estime_max > 0 && (
                        <div className="text-xs text-muted-foreground mt-0.5" data-testid="dashboard-cfe-fourchette">
                          entre {formatCurrency(data?.cfe?.estimation?.montant_estime_min)} et {formatCurrency(data?.cfe?.estimation?.montant_estime_max)} selon ta commune
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-3 text-center space-y-1">
                  <div>Base : CA {data?.cfe?.annee_reference} = {formatCurrency(data?.cfe?.ca_reference || 0)}</div>
                  {data?.cfe?.estimation?.note && <div className="italic">{data?.cfe?.estimation?.note}</div>}
                </div>
              </div>
            ) : (
              <div className="text-center p-4 rounded-lg" style={{backgroundColor: COLORS.success + '15'}}>
                <div className="text-2xl mb-2">🎉</div>
                <div className="font-bold" style={{color: COLORS.success}}>Exonéré de CFE</div>
                <div className="text-sm text-muted-foreground mt-2">{data?.cfe?.raison}</div>
                {data?.cfe?.ca_reference > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">CA {data?.cfe?.annee_reference} : {formatCurrency(data?.cfe?.ca_reference)}</div>
                )}
              </div>
            )}
          </div>

          {/* 9. MA RETRAITE */}
          <div className="rounded-xl border p-6" style={{backgroundColor: COLORS.bg}}>
            <h3 className="font-semibold mb-4" style={{color: COLORS.accent}}>MA RETRAITE</h3>
            <div className="text-center">
              <div className="text-5xl mb-2">{getTrimestreEmoji(data?.trimestres_retraite)}</div>
              <div className="text-4xl font-bold" style={{color: COLORS.text}}>{data?.trimestres_retraite}</div>
              <div className="text-sm text-muted-foreground">trimestre(s) validé(s)</div>
              <div className="flex justify-center gap-2 mt-4">
                {[1, 2, 3, 4].map((t) => (
                  <div key={t} className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold transition-all"
                       style={{
                         backgroundColor: t <= data?.trimestres_retraite ? COLORS.principale : COLORS.secondaire,
                         color: t <= data?.trimestres_retraite ? COLORS.text : COLORS.accent,
                         border: t <= data?.trimestres_retraite ? `2px solid ${COLORS.text}` : `2px solid ${COLORS.accent}40`
                       }}>
                    {t <= data?.trimestres_retraite ? '✓' : t}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mini card ACRE si actif */}
          {data?.profil?.acre_actif && (
            <div className="rounded-xl border p-4" style={{backgroundColor: COLORS.success + '15', borderColor: COLORS.success}}>
              <div className="flex items-center gap-2">
                <PartyPopper size={18} style={{color: COLORS.success}} />
                <span className="text-sm font-medium" style={{color: COLORS.success}}>ACRE actif</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Cotisations réduites la 1ère année</div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};
